import { useUserData } from '../../../common/hooks/useUserData';
import { useRouter } from 'next/router';
import { ReactNode, useEffect } from 'react';
import { MainLayout } from '../../../common/containers/MainLayout/MainLayout';
import Link from 'next/link';
import styled from 'styled-components';

import * as React from 'react';

import {
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useSnackbar } from 'notistack';
import { spacings } from '../../../common/styles/spacings';
import { Trans } from '@lingui/macro';
import { ForbiddenScreen } from '../../../common/components/Forbidden';

type Props = {
  children: ReactNode;
};

const adminRoutes = [
  {
    title: 'Přehled',
    href: '/admin',
  },
  {
    title: 'Všechny projekty',
    href: '/admin/projects',
  },
];

const sources = ['howto', 'blog'];

enum SourcesMap {
  'howto' = 'CMS stránky',
  'blog' = 'Blogové články',
}

export const AdminPageWrapper = ({ children }: Props) => {
  const { isAdmin, user } = useUserData();
  const router = useRouter();
  const title = adminRoutes.find(
    (route) => route.href === router.pathname
  ).title;
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);

  const handleClick = () => {
    setOpen(!open);
  };

  const selectSource = (source: string) => {
    setOpen(false);
    handleDataSync(source);
  };

  useEffect(() => {
    if (user && !isAdmin) {
      router.push('/404');
    }
  }, []);

  const handleError = (source: string, data: any) => {
    console.error(`Error updating ${source} source: `, data);
    enqueueSnackbar(
      `Něco se pokazilo při aktualizaci typu: ${SourcesMap[source]}`,
      {
        variant: 'error',
      }
    );
  };

  const handleDataSync = async (source: string) => {
    setLoading(true);
    try {
      const response = await fetch('/api/sources/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sources: [source],
        }),
      });

      const data = await response.json();

      if (data.status !== 'ok') {
        setLoading(false);
        handleError(source, data);
        return;
      }

      console.log(`${source} source updated succesfully`);
      enqueueSnackbar(`Úspěšně aktualizováno: ${SourcesMap[source]}`, {
        variant: 'success',
      });
    } catch (error) {
      handleError(source, error);
    }
    setLoading(false);
  };

  return (
    <MainLayout title="Admin">
      {isAdmin ? (
        <>
          <Grid container spacing={2} marginBottom={4}>
            {adminRoutes.map((route) => (
              <Grid item key={route.href}>
                <Link href={route.href}>{route.title}</Link>
              </Grid>
            ))}
          </Grid>

          <RegenerateSection>
            <Typography variant="h4">
              <Trans>Aktualizace AI zdrojů</Trans>
            </Typography>
            {loading ? (
              <CircularProgress />
            ) : (
              <div>
                <Button
                  variant="contained"
                  ref={buttonRef}
                  onClick={handleClick}
                >
                  <Trans>Aktualizovat</Trans>
                </Button>

                <Menu
                  anchorEl={buttonRef.current}
                  open={open}
                  onClose={() => setOpen(false)}
                >
                  {sources.map((source) => (
                    <MenuItem key={source} onClick={() => selectSource(source)}>
                      {SourcesMap[source]}
                    </MenuItem>
                  ))}
                </Menu>
              </div>
            )}
          </RegenerateSection>

          <Typography variant="h1" gutterBottom>
            {title}
          </Typography>

          {children}
        </>
      ) : (
        <ForbiddenScreen />
      )}
    </MainLayout>
  );
};

const RegenerateSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacings.px8};
  align-items: flex-end;
`;
